import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { CollectStep, FlowStep, FlowStepType } from "../../../types";

const TYPE_COLORS: Record<FlowStepType, "default" | "primary" | "secondary" | "success" | "warning" | "error" | "info"> = {
  collect: "primary",
  decision: "warning",
  tool_call: "secondary",
  action: "secondary",
  route: "info",
  message: "default",
  end: "error",
};

const ALL_TYPES: FlowStepType[] = [
  "collect",
  "decision",
  "tool_call",
  "action",
  "route",
  "message",
  "end",
];

interface Props {
  steps: FlowStep[];
  selectedStepId: string | null;
  onSelect: (id: string) => void;
  onAdd: (type: FlowStepType) => void;
  onRemove: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
}

export const StepList = ({
  steps,
  selectedStepId,
  onSelect,
  onAdd,
  onRemove,
  onReorder,
}: Props) => {
  const [newType, setNewType] = useState<FlowStepType>("collect");
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const sorted = [...steps].sort((a, b) => a.order - b.order);

  // Sposta dragId nella posizione di targetId e ricalcola l'ordine.
  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const ids = sorted.map((s) => s.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) {
      setDragId(null);
      setOverId(null);
      return;
    }
    ids.splice(from, 1);
    ids.splice(to, 0, dragId);
    onReorder(ids);
    setDragId(null);
    setOverId(null);
  };

  return (
    <Box className="flex-col gap-2">
      <Typography variant="subtitle2">Step ({steps.length})</Typography>

      <List dense disablePadding sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
        {sorted.length === 0 && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nessuno step. Aggiungine uno.
            </Typography>
          </Box>
        )}
        {sorted.map((step, idx) => (
          <React.Fragment key={step.id}>
            {idx > 0 && <Divider />}
            <ListItemButton
              selected={selectedStepId === step.id}
              onClick={() => onSelect(step.id)}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                if (overId !== step.id) setOverId(step.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(step.id);
              }}
              sx={{
                pr: 1,
                opacity: dragId === step.id ? 0.4 : 1,
                borderTop:
                  overId === step.id && dragId && dragId !== step.id
                    ? "2px solid"
                    : "2px solid transparent",
                borderTopColor:
                  overId === step.id && dragId && dragId !== step.id
                    ? "primary.main"
                    : "transparent",
              }}
            >
              <Tooltip title="Trascina per riordinare">
                <Box
                  component="span"
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    setDragId(step.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "grab",
                    color: "text.disabled",
                    mr: 0.5,
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 18 }} />
                </Box>
              </Tooltip>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Chip
                      label={step.type}
                      size="small"
                      color={TYPE_COLORS[step.type]}
                      sx={{ fontSize: "0.65rem", height: 18 }}
                    />
                    {step.type === "collect" && (step as CollectStep).conversation_flexibility === "medium" && (
                      <Chip label="Media" size="small" color="default" sx={{ fontSize: "0.6rem", height: 16 }} />
                    )}
                    {step.type === "collect" && (step as CollectStep).conversation_flexibility === "high" && (
                      <Chip label="Alta" size="small" color="primary" sx={{ fontSize: "0.6rem", height: 16 }} />
                    )}
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {step.id.slice(0, 8)}…
                    </Typography>
                  </Box>
                }
              />
              <Tooltip title="Rimuovi step">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(step.id);
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Tipo step</InputLabel>
          <Select
            label="Tipo step"
            value={newType}
            onChange={(e) => setNewType(e.target.value as FlowStepType)}
          >
            {ALL_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAdd(newType)}
        >
          Aggiungi
        </Button>
      </Box>
    </Box>
  );
};
